import { Link } from "react-router-dom";
import { ArrowRight, Heart, Shield, Users, Sprout, HandCoins, Activity, Accessibility, Wheat, Wrench, Ambulance, Tractor, Dam, Leaf, Link as LinkIcon, Lightbulb, Hospital, LifeBuoy } from "lucide-react";
import { Button } from "@/components/ui/button";
import heroImg from '../assets/hero.jpg';
import healthImg from '../assets/health.jpg';
import childImg from '../assets/child-protection.jpg';
import climateImg from '../assets/climate.jpg';


const focusAreas = [
  {
    icon: Tractor,
    title: "Livestock & Agriculture Market Systems Development (Core Focus)",
    desc: (
      <ul className="mt-2 space-y-2 text-sm leading-6 text-muted-foreground transition-colors duration-200 group-hover:text-primary">
        <li className="flex items-start gap-2">
          <Leaf className="mt-1 h-4 w-4 shrink-0 text-emerald-500 transition-colors duration-200" />
          <span>Strengthens pastoral and agro-pastoral economies through livestock and agriculture value chains.</span>
        </li>
        <li className="flex items-start gap-2">
          <span className="mt-1 block h-4 w-4 text-[1.1rem] transition-colors duration-200">🐪</span>
          <span>Enhances productivity with animal health services, climate-smart agriculture, and improved input systems.</span>
        </li>
        <li className="flex items-start gap-2">
          <LinkIcon className="mt-1 h-4 w-4 shrink-0 text-current transition-colors duration-200" />
          <span>Advances aggregation, processing, and market linkages to connect producers with markets.</span>
        </li>
        <li className="flex items-start gap-2">
          <Users className="mt-1 h-4 w-4 shrink-0 text-current transition-colors duration-200" />
          <span>Collaborates with cooperatives, SMEs, and private sector actors to drive inclusive growth.</span>
        </li>
        <li className="flex items-start gap-2">
          <Lightbulb className="mt-1 h-4 w-4 shrink-0 text-current transition-colors duration-200" />
          <span>Supports communities in transitioning from subsistence production to market-oriented, income-generating systems.</span>
        </li>
      </ul>
    ),
  },
  { icon: Users, title: "Economic Empowerment & Private Sector Engagement", desc: (
    <ul className="mt-2 space-y-2 text-sm leading-6 text-muted-foreground transition-colors duration-200 group-hover:text-primary">
      <li className="flex items-start gap-2">
        <span className="mt-1 block h-4 w-4 text-[1.1rem] transition-colors duration-200">💼</span>
        <span>Promotes sustainable livelihoods through enterprise development, job creation, and financial inclusion.</span>
      </li>
      <li className="flex items-start gap-2">
        <span className="mt-1 block h-4 w-4 text-[1.1rem] transition-colors duration-200">👩‍🦱👨‍🦱</span>
        <span>Prioritizes women and youth in economic empowerment initiatives.</span>
      </li>
      <li className="flex items-start gap-2">
        <span className="mt-1 block h-4 w-4 text-[1.1rem] transition-colors duration-200">🏢</span>
        <span>Strengthens cooperatives and incubates SMEs to support local businesses.</span>
      </li>
      <li className="flex items-start gap-2">
        <span className="mt-1 block h-4 w-4 text-[1.1rem] transition-colors duration-200">💳</span>
        <span>Expands access to finance, from VSLA groups to enterprise growth.</span>
      </li>
      <li className="flex items-start gap-2">
        <span className="mt-1 block h-4 w-4 text-[1.1rem] transition-colors duration-200">📈</span>
        <span>Provides business development services to enhance competitiveness.</span>
      </li>
      <li className="flex items-start gap-2">
        <span className="mt-1 block h-4 w-4 text-[1.1rem] transition-colors duration-200">🌍</span>
        <span>Facilitates market access and commercialization, enabling participation in local and cross-border markets.</span>
      </li>
    </ul>
  ) },
  { icon: Sprout, title: "Climate Change Adaptation & Natural Resource Management", desc: (
    <ul className="mt-2 space-y-2 text-sm leading-6 text-muted-foreground transition-colors duration-200 group-hover:text-primary">
      <li className="flex items-start gap-2">
        <span className="mt-1 block h-4 w-4 text-[1.1rem] transition-colors duration-200">🌍</span>
        <span>Enhances resilience to climate shocks through climate-smart and nature-based solutions.</span>
      </li>
      <li className="flex items-start gap-2">
        <span className="mt-1 block h-4 w-4 text-[1.1rem] transition-colors duration-200">🌱</span>
        <span>Restores ecosystems via rangeland rehabilitation and sustainable land management.</span>
      </li>
      <li className="flex items-start gap-2">
        <span className="mt-1 block h-4 w-4 text-[1.1rem] transition-colors duration-200">🌾</span>
        <span>Develops drought-resilient fodder systems to secure livestock feed.</span>
      </li>
      <li className="flex items-start gap-2">
        <span className="mt-1 block h-4 w-4 text-[1.1rem] transition-colors duration-200">💧</span>
        <span>Expands water resource development for communities in vulnerable areas.</span>
      </li>
      <li className="flex items-start gap-2">
        <span className="mt-1 block h-4 w-4 text-[1.1rem] transition-colors duration-200">🚨</span>
        <span>Integrates community-based disaster risk reduction (DRR) and early warning systems.</span>
      </li>
      <li className="flex items-start gap-2">
        <span className="mt-1 block h-4 w-4 text-[1.1rem] transition-colors duration-200">🌊</span>
        <span>Strengthens adaptive capacity in drought- and flood-prone regions.</span>
      </li>
    </ul>
  ) },
  { icon: Ambulance, title: "Humanitarian Response & Resilience", desc: (
    <ul className="mt-2 space-y-2 text-sm leading-6 text-muted-foreground transition-colors duration-200 group-hover:text-primary">
      <li className="flex items-start gap-2">
        <span className="mt-1 block h-4 w-4 text-[1.1rem] transition-colors duration-200">🚑</span>
        <span>Delivers timely and effective emergency assistance in crisis situations.</span>
      </li>
      <li className="flex items-start gap-2">
        <span className="mt-1 block h-4 w-4 text-[1.1rem] transition-colors duration-200">🔄</span>
        <span>Ensures smooth transition from relief to long-term recovery and resilience building.</span>
      </li>
      <li className="flex items-start gap-2">
        <span className="mt-1 block h-4 w-4 text-[1.1rem] transition-colors duration-200">💵</span>
        <span>Implements cash-based interventions to support affected households.</span>
      </li>
      <li className="flex items-start gap-2">
        <span className="mt-1 block h-4 w-4 text-[1.1rem] transition-colors duration-200">💼</span>
        <span>Supports livelihood recovery programs to restore economic activity.</span>
      </li>
      <li className="flex items-start gap-2">
        <span className="mt-1 block h-4 w-4 text-[1.1rem] transition-colors duration-200">🛡️</span>
        <span>Strengthens disaster preparedness systems for future shocks.</span>
      </li>
      <li className="flex items-start gap-2">
        <span className="mt-1 block h-4 w-4 text-[1.1rem] transition-colors duration-200">🌐</span>
        <span>Enhances access to services in hard-to-reach areas through cross-border coordination and strategic logistics (including planned response hubs).</span>
      </li>
    </ul>
  ) },
  { icon: Wheat, title: "Health, Nutrition & Food Systems Strengthening", desc: (
    <ul className="mt-2 space-y-2 text-sm leading-6 text-muted-foreground transition-colors duration-200 group-hover:text-primary">
      <li className="flex items-start gap-2">
        <span className="mt-1 block h-4 w-4 text-[1.1rem] transition-colors duration-200">🏥</span>
        <span>Improves community well-being through integrated health, nutrition, and food security interventions.</span>
      </li>
      <li className="flex items-start gap-2">
        <span className="mt-1 block h-4 w-4 text-[1.1rem] transition-colors duration-200">👩‍👧</span>
        <span>Supports maternal and child health programs.</span>
      </li>
      <li className="flex items-start gap-2">
        <span className="mt-1 block h-4 w-4 text-[1.1rem] transition-colors duration-200">🍎</span>
        <span>Promotes community nutrition initiatives and nutrition-sensitive agriculture.</span>
      </li>
      <li className="flex items-start gap-2">
        <span className="mt-1 block h-4 w-4 text-[1.1rem] transition-colors duration-200">🌾</span>
        <span>Links food production with nutrition outcomes to reduce malnutrition.</span>
      </li>
      <li className="flex items-start gap-2">
        <span className="mt-1 block h-4 w-4 text-[1.1rem] transition-colors duration-200">🏡</span>
        <span>Strengthens household food security and resilience.</span>
      </li>
    </ul>
  ) },
  { icon: Dam, title: "Water, Sanitation & Hygiene (WASH)", desc: (
    <ul className="mt-2 space-y-2 text-sm leading-6 text-muted-foreground transition-colors duration-200 group-hover:text-primary">
      <li className="flex items-start gap-2">
        <span className="mt-1 block h-4 w-4 text-[1.1rem] transition-colors duration-200">💧</span>
        <span>Expands access to safe water and sanitation services through sustainable infrastructure.</span>
      </li>
      <li className="flex items-start gap-2">
        <span className="mt-1 block h-4 w-4 text-[1.1rem] transition-colors duration-200">🛠️</span>
        <span>Develops boreholes and rehabilitates water systems to improve reliability.</span>
      </li>
      <li className="flex items-start gap-2">
        <span className="mt-1 block h-4 w-4 text-[1.1rem] transition-colors duration-200">🚻</span>
        <span>Provides sanitation facilities to enhance community hygiene.</span>
      </li>
      <li className="flex items-start gap-2">
        <span className="mt-1 block h-4 w-4 text-[1.1rem] transition-colors duration-200">🧼</span>
        <span>Promotes hygiene behavior change for healthier practices.</span>
      </li>
      <li className="flex items-start gap-2">
        <span className="mt-1 block h-4 w-4 text-[1.1rem] transition-colors duration-200">🔗</span>
        <span>Integrates WASH programming with health and nutrition interventions.</span>
      </li>
    </ul>
  ) },
  { icon: Activity, title: "Gender Equality, Youth & Social Inclusion", desc: (
    <ul className="mt-2 space-y-2 text-sm leading-6 text-muted-foreground transition-colors duration-200 group-hover:text-primary">
      <li className="flex items-start gap-2">
        <span className="mt-1 block h-4 w-4 text-[1.1rem] transition-colors duration-200">👩‍🦱</span>
        <span>Mainstreams gender and inclusion across all programs for equitable access.</span>
      </li>
      <li className="flex items-start gap-2">
        <span className="mt-1 block h-4 w-4 text-[1.1rem] transition-colors duration-200">💪</span>
        <span>Promotes women's economic empowerment and leadership development.</span>
      </li>
      <li className="flex items-start gap-2">
        <span className="mt-1 block h-4 w-4 text-[1.1rem] transition-colors duration-200">🧑‍🤝‍🧑</span>
        <span>Engages youth in value chains and enterprise opportunities.</span>
      </li>
      <li className="flex items-start gap-2">
        <span className="mt-1 block h-4 w-4 text-[1.1rem] transition-colors duration-200">🛡️</span>
        <span>Addresses barriers such as gender-based violence and social exclusion.</span>
      </li>
      <li className="flex items-start gap-2">
        <span className="mt-1 block h-4 w-4 text-[1.1rem] transition-colors duration-200">🌍</span>
        <span>Prioritizes vulnerable and marginalized groups (VMGs) in interventions.</span>
      </li>
    </ul>
  ) },
  { icon: Users, title: "Basic Services & Human Capital Development", desc: (
    <ul className="mt-2 space-y-2 text-sm leading-6 text-muted-foreground transition-colors duration-200 group-hover:text-primary">
      <li className="flex items-start gap-2">
        <span className="mt-1 block h-4 w-4 text-[1.1rem] transition-colors duration-200">📚</span>
        <span>Supports access to quality education and essential social services.</span>
      </li>
      <li className="flex items-start gap-2">
        <span className="mt-1 block h-4 w-4 text-[1.1rem] transition-colors duration-200">🏫</span>
        <span>Rehabilitates schools to strengthen learning environments.</span>
      </li>
      <li className="flex items-start gap-2">
        <span className="mt-1 block h-4 w-4 text-[1.1rem] transition-colors duration-200">🛠️</span>
        <span>Provides skills development and training opportunities.</span>
      </li>
      <li className="flex items-start gap-2">
        <span className="mt-1 block h-4 w-4 text-[1.1rem] transition-colors duration-200">👩‍🏫</span>
        <span>Builds capacity of service providers for better delivery.</span>
      </li>
      <li className="flex items-start gap-2">
        <span className="mt-1 block h-4 w-4 text-[1.1rem] transition-colors duration-200">🤝</span>
        <span>Ensures service delivery is inclusive, community-driven, and aligned with local systems.</span>
      </li>
    </ul>
  ) },
  { icon: Hospital, title: "Human Rights & Social Protection", desc: (
    <ul className="mt-2 space-y-2 text-sm leading-6 text-muted-foreground transition-colors duration-200 group-hover:text-primary">
      <li className="flex items-start gap-2">
        <span className="mt-1 block h-4 w-4 text-[1.1rem] transition-colors duration-200">⚖️</span>
        <span>Integrates human rights advocacy and social protection across all programs.</span>
      </li>
      <li className="flex items-start gap-2">
        <span className="mt-1 block h-4 w-4 text-[1.1rem] transition-colors duration-200">🧒</span>
        <span>Actively promotes child protection to safeguard vulnerable groups.</span>
      </li>
      <li className="flex items-start gap-2">
        <span className="mt-1 block h-4 w-4 text-[1.1rem] transition-colors duration-200">🛡️</span>
        <span>Combats gender-based violence and addresses social exclusion.</span>
      </li>
      <li className="flex items-start gap-2">
        <span className="mt-1 block h-4 w-4 text-[1.1rem] transition-colors duration-200">👥</span>
        <span>Ensures marginalized populations have access to justice and services.</span>
      </li>
      <li className="flex items-start gap-2">
        <span className="mt-1 block h-4 w-4 text-[1.1rem] transition-colors duration-200">🏛️</span>
        <span>Works closely with government institutions and community leaders.</span>
      </li>
      <li className="flex items-start gap-2">
        <span className="mt-1 block h-4 w-4 text-[1.1rem] transition-colors duration-200">📜</span>
        <span>Strengthens legal frameworks to protect against exploitation, discrimination, and abuse.</span>
      </li>
    </ul>
  ) },
  { icon: Leaf, title: "Environmental Sustainability & Green Solutions", desc: (
    <ul className="mt-2 space-y-2 text-sm leading-6 text-muted-foreground transition-colors duration-200 group-hover:text-primary">
      <li className="flex items-start gap-2">
        <span className="mt-1 block h-4 w-4 text-[1.1rem] transition-colors duration-200">🌱</span>
        <span>Promotes sustainable environmental management through ecosystem restoration.</span>
      </li>
      <li className="flex items-start gap-2">
        <span className="mt-1 block h-4 w-4 text-[1.1rem] transition-colors duration-200">🌳</span>
        <span>Implements reforestation and land restoration initiatives.</span>
      </li>
      <li className="flex items-start gap-2">
        <span className="mt-1 block h-4 w-4 text-[1.1rem] transition-colors duration-200">☀️</span>
        <span>Advances renewable energy solutions and clean energy adoption.</span>
      </li>
      <li className="flex items-start gap-2">
        <span className="mt-1 block h-4 w-4 text-[1.1rem] transition-colors duration-200">📘</span>
        <span>Provides climate education to strengthen awareness and action.</span>
      </li>
      <li className="flex items-start gap-2">
        <span className="mt-1 block h-4 w-4 text-[1.1rem] transition-colors duration-200">🌍</span>
        <span>Contributes to reduced environmental degradation and enhanced community resilience.</span>
      </li>
    </ul>
  ) },
  { icon: LifeBuoy, title: "Timely Humanitarian Response", desc: (
    <ul className="mt-2 space-y-2 text-sm leading-6 text-muted-foreground transition-colors duration-200 group-hover:text-primary">
      <li className="flex items-start gap-2">
        <span className="mt-1 block h-4 w-4 text-[1.1rem] transition-colors duration-200">🍲</span>
        <span>Provides food aid to communities affected by disasters, conflict, and displacement.</span>
      </li>
      <li className="flex items-start gap-2">
        <span className="mt-1 block h-4 w-4 text-[1.1rem] transition-colors duration-200">🏠</span>
        <span>Ensures access to shelter for crisis‑affected populations.</span>
      </li>
      <li className="flex items-start gap-2">
        <span className="mt-1 block h-4 w-4 text-[1.1rem] transition-colors duration-200">🏥</span>
        <span>Delivers healthcare services in emergencies.</span>
      </li>
      <li className="flex items-start gap-2">
        <span className="mt-1 block h-4 w-4 text-[1.1rem] transition-colors duration-200">💙</span>
        <span>Offers psychosocial support to strengthen recovery.</span>
      </li>
      <li className="flex items-start gap-2">
        <span className="mt-1 block h-4 w-4 text-[1.1rem] transition-colors duration-200">⚡</span>
        <span>Operates rapid response mechanisms to deliver aid efficiently.</span>
      </li>
      <li className="flex items-start gap-2">
        <span className="mt-1 block h-4 w-4 text-[1.1rem] transition-colors duration-200">🔄</span>
        <span>Facilitates early recovery interventions to mitigate crisis impacts.</span>
      </li>
    </ul>
  ) },
];

const Home = () => {
  return (
    <>
      {/* HERO */}
      <section className="relative isolate overflow-hidden">
        <img
          src={heroImg}
          alt="Community gathered at a rural water point at golden hour"
          width={1920}
          height={1280}
          className="absolute inset-0 -z-10 h-full w-full object-cover"
        />
        <div className="absolute inset-0 -z-10" style={{ background: "var(--gradient-hero)" }} />
        <div className="container-wide flex min-h-[78vh] flex-col justify-center py-24 text-primary-foreground">
          <p className="mb-4 text-sm font-semibold uppercase tracking-[0.25em] text-accent animate-fade-in">
            Climate Resilience · Development · Dignity
          </p>
          <h1 className="max-w-4xl font-serif text-4xl font-semibold leading-[1.1] md:text-6xl lg:text-7xl animate-fade-up">
            Building resilient communities across the Horn of Africa.
          </h1>
          <p className="mt-6 max-w-2xl text-lg leading-relaxed text-primary-foreground/85 md:text-xl animate-fade-up">
            We work alongside communities to deliver lifesaving services, strengthen
            climate adaptation, and unlock long-term development.
          </p>
          <div className="mt-10 flex flex-wrap gap-4 animate-fade-up">
            <Button asChild size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90">
              <Link to="/donate">Support our work <ArrowRight className="ml-2 h-4 w-4" /></Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-primary-foreground/40 bg-transparent text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground">
              <Link to="/programs">Explore programs</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* INTRO */}
      <section className="section-padding">
        <div className="container-narrow grid gap-12 md:grid-cols-5">
          <div className="md:col-span-2">
            <p className="text-sm font-semibold uppercase tracking-[0.2em] text-accent">Who we are</p>
            <h2 className="mt-3 font-serif text-3xl font-semibold leading-tight text-primary md:text-4xl">
              A community-led organization for resilience and dignity.
            </h2>
          </div>
          <div className="space-y-4 text-base leading-relaxed text-muted-foreground md:col-span-3 md:text-lg">
            <p>
              Founded by humanitarian practitioners with deep roots in Somali communities, we operate
              across drought-affected and climate-vulnerable regions to bridge emergency response and
              long-term recovery.
            </p>
            <p>
              From mobile health clinics in remote villages to climate adaptation projects in
              pastoralist communities, our work is locally led, evidence-based, and built to last.
            </p>
            <Link to="/about" className="inline-flex items-center gap-2 pt-2 font-medium text-primary hover:text-accent">
              Learn more about us <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* IMPACT BANNER */}
      <section className="bg-primary py-16 text-foreground">
        <div className="container-wide grid grid-cols-2 gap-8 text-center md:grid-cols-5">
          {[
            { n: "370,168+", l: "Beneficiaries reached" },
            { n: "3", l: "Countries of operation" },
            { n: "15+", l: "Implementing partners" },
            { n: "12 yrs", l: "On-the-ground experience" },
            { n: "35+", l: "Years of expertise experience" },
          ].map((s) => (
            <div key={s.l}>
              <p className="font-serif text-4xl font-semibold text-yellow-900 md:text-5xl">{s.n}</p>
              <p className="mt-2 text-sm uppercase tracking-wider text-foreground/70">{s.l}</p>
            </div>
          ))}
        </div>
      </section>

      {/* STRATEGIC FOCUS AREAS */}
      <section className="section-padding bg-secondary">
        <div className="container-wide">
          <div className="mb-14 max-w-2xl">
            <p className="text-sm font-semibold uppercase tracking-[0.2em] text-accent">Strategic Focus Areas</p>
            <h2 className="mt-3 font-serif text-3xl font-semibold text-foreground md:text-4xl">
              CRDO has transitioned from a humanitarian response organization to a market-driven resilience and development actor, delivering integrated solutions across fragile and climate-affected contexts. Through community engagement, institutional strengthening, private sector partnerships, and technology-driven approaches, CRDO enhances livelihoods, resilience, and inclusive economic growth.
            </h2>
            <h2 className="mt-3 font-serif text-3xl font-semibold text-primary md:text-4xl">
              The key focus areas include:             </h2>
          </div>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {focusAreas.map(({ icon: Icon, title, desc }) => (
              <div
                key={title}
                className="group rounded-lg border border-border bg-card p-6 transition-all hover:-translate-y-1 hover:shadow-[var(--shadow-elegant)]"
              >
                <span className="grid h-11 w-11 place-items-center rounded-md bg-primary/10 text-primary transition-colors group-hover:bg-accent group-hover:text-accent-foreground">
                  <Icon className="h-5 w-5" />
                </span>
                <h3 className="mt-5 font-serif text-lg font-semibold text-primary">{title}</h3>
                <div>{desc}</div>
              </div>
            ))}
          </div>
          <div className="mt-10">
            <Button asChild variant="outline" className="border-primary text-primary hover:bg-primary hover:text-primary-foreground">
              <Link to="/programs">All programs <ArrowRight className="ml-2 h-4 w-4" /></Link>
            </Button>
          </div>
        </div>
      </section>

      {/* FEATURED IMAGES */}
      <section className="section-padding">
        <div className="container-wide grid gap-6 md:grid-cols-3">
          {[
            { img: healthImg, t: "Health & Nutrition", d: "Lifesaving care where it's needed most." },
            { img: childImg, t: "Child Protection", d: "Childhoods worth protecting." },
            { img: climateImg, t: "Climate Action", d: "Adaptation rooted in local knowledge." },
          ].map((c) => (
            <article key={c.t} className="group overflow-hidden rounded-lg bg-card shadow-[var(--shadow-card)]">
              <div className="aspect-[4/3] overflow-hidden">
                <img src={c.img} alt={c.t} loading="lazy" width={1024} height={768} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
              </div>
              <div className="p-6">
                <h3 className="font-serif text-xl font-semibold text-primary">{c.t}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{c.d}</p>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* ORGANIZATION EXPERIENCE AND EXPERTISE */}
      <section className="section-padding bg-secondary">
        <div className="container-wide">
          <div className="mb-14 max-w-2xl">
            <p className="text-sm font-semibold uppercase tracking-[0.2em] text-accent">Organization Experience and Expertise</p>
            <h2 className="mt-3 font-serif text-3xl font-semibold text-primary md:text-4xl">
              CRDO remains committed to collaborative partnerships, knowledge-sharing, and innovative programming to create long-term,
               sustainable solutions for vulnerable populations. 
              CRDO has developed significant expertise in implementing high-impact programs that address the needs of marginalized communities.
            </h2>
          </div>
        </div>
      </section>

      {/* DONATE CTA */}
      <section className="bg-gradient-to-br from-primary via-primary to-primary-glow py-20 text-foreground">
        <div className="container-narrow text-center">
          <h2 className="font-serif text-3xl font-semibold md:text-5xl">Stand with the Horn of Africa communities.</h2>
          <p className="mx-auto mt-5 max-w-xl text-lg text-foreground/85">
            Every contribution funds clinics, classrooms, water points, and climate-smart livelihoods.
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <Button asChild size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90">
               <Link to="/donate">Donate now</Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-foreground/40 bg-transparent text-foreground hover:bg-foreground/10 hover:text-foreground">
              <Link to="/contact">Partner with us</Link>
            </Button>
          </div>
        </div>
      </section>
    </>
  );
};

export default Home;
